import {
  forwardRef,
  type KeyboardEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import { cn } from '../../../lib/utils';

/**
 * Ô sửa tại chỗ của trình soạn sổ — APM-DSX-e (plan §11.4).
 *
 * Trông như chữ của chế độ Xem: không viền, nền trong suốt; viền hiện khi
 * hover, viền màu chủ đạo khi đang gõ. Lề trái âm bằng đúng phần đệm, nên chữ
 * trong ô thẳng hàng với chữ ở chế độ Xem — chuyển qua lại không thấy giật.
 *
 * Luôn là `<textarea>` cao theo nội dung: tiêu đề dài gãy dòng như ở chế độ
 * Xem thay vì bị cắt trong một `<input>` một dòng. `multiline = false` thì
 * Enter **không** xuống dòng (gọi `onEnter` nếu có, không thì rời ô) — tiêu đề
 * và tên phương án là một dòng.
 *
 * Bo góc theo `--radius-field` (12px) nhờ luật chung cho mọi ô nhập.
 */
export const InlineText = forwardRef<
  HTMLTextAreaElement,
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'> & {
    readonly value: string;
    readonly onValueChange: (value: string) => void;
    readonly multiline?: boolean;
    /** Enter ở ô một dòng. Bỏ trống ⇒ rời ô (lưu). */
    readonly onEnter?: (() => void) | undefined;
  }
>(function InlineText(
  { value, onValueChange, multiline = false, onEnter, onKeyDown, className, ...props },
  forwarded,
): ReactNode {
  const ref = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(forwarded, () => ref.current!, []);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    fitHeight(element);
  }, [value]);

  // Bề rộng đổi (khung hẹp lại, xoay máy, bố cục co sau khi gắn) cũng đổi số dòng:
  // chỉ đo theo `value` thì chữ dài bị cắt ở một dòng sau `overflow-hidden`.
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let width = element.clientWidth;
    const observer = new ResizeObserver(() => {
      if (element.clientWidth === width) return;
      width = element.clientWidth;
      fitHeight(element);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const keyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (!multiline && event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      if (onEnter) onEnter();
      else event.currentTarget.blur();
    }
  };

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      onKeyDown={keyDown}
      className={cn(
        '-mx-1.5 block w-[calc(100%+0.75rem)] min-w-0 resize-none overflow-hidden border border-transparent bg-transparent px-1.5 py-0.5 transition-colors [overflow-wrap:anywhere]',
        'placeholder:font-normal placeholder:text-muted-foreground/70',
        'hover:border-border focus:border-primary focus:bg-background focus:outline-none',
        'disabled:cursor-not-allowed disabled:hover:border-transparent',
        'aria-invalid:border-destructive/70',
        className,
      )}
      {...props}
    />
  );
});

function fitHeight(element: HTMLTextAreaElement): void {
  element.style.height = 'auto';
  element.style.height = `${element.scrollHeight}px`;
}
